import { useAuth } from "@/hooks/useAuth";
import { useAttemptHistory, useUserStats } from "@/hooks/useExam";
import StatsCard from "@/components/dashboard/StatsCard";
import AttemptHistory from "@/components/dashboard/AttemptHistory";
import ProgressChart from "@/components/dashboard/ProgressChart";
import { format } from "date-fns";

export default function Profile() {
  const { user } = useAuth();
  const { data: stats } = useUserStats();
  const { data: historyData } = useAttemptHistory(1, 10);

  return (
    <div className="page-enter mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      {/* Profile header */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm mb-8">
        <div className="flex items-center space-x-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xl font-bold">
            {user?.first_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "U"}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {user?.first_name && user?.last_name
                ? `${user.first_name} ${user.last_name}`
                : user?.email || "User"}
            </h1>
            <p className="text-sm text-gray-500">{user?.email}</p>
            {user?.created_at && (
              <p className="text-xs text-gray-400 mt-0.5">
                Member since {format(new Date(user.created_at), "MMMM yyyy")}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Stats grid */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatsCard
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            }
            label="Exams Taken"
            value={stats.completed_attempts}
            color="blue"
          />
          <StatsCard
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
            label="Average Score"
            value={stats.average_score !== null ? `${Math.round(stats.average_score)}%` : "N/A"}
            color="purple"
          />
          <StatsCard
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            label="Pass Rate"
            value={stats.pass_rate !== null ? `${Math.round(stats.pass_rate)}%` : "N/A"}
            color="green"
          />
          <StatsCard
            icon={
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            }
            label="Best Score"
            value={stats.best_score !== null ? `${stats.best_score}/50` : "N/A"}
            color="yellow"
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Progress chart */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Score Progress</h2>
          <ProgressChart attempts={historyData || []} />
        </div>

        {/* Recent attempts */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Recent Attempts</h2>
          </div>
          <AttemptHistory attempts={(historyData || []).slice(0, 5)} />
        </div>
      </div>
    </div>
  );
}
